import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export function SeatManagementCard() {
  const { mode, role, dealershipId, isSolo } = useAuth();
  const { subscription, isActive } = useSubscription();
  const [usedSeats, setUsedSeats] = useState<number | null>(null);

  const isDealershipPlan =
    subscription?.price_id === "dealership_monthly" ||
    subscription?.price_id === "dealership_yearly";

  useEffect(() => {
    let cancel = false;
    async function load() {
      if (!dealershipId) return;
      // Count distinct user_ids in user_roles for this dealership
      const { data } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("dealership_id", dealershipId);
      if (cancel) return;
      const distinct = new Set((data ?? []).map((r) => r.user_id));
      setUsedSeats(distinct.size);
    }
    load();
    return () => { cancel = true; };
  }, [dealershipId]);

  if (mode !== "real") return null;
  if (role !== "manager" && role !== "admin") return null;
  if (isSolo) return null;
  if (!isActive || !isDealershipPlan) return null;

  const purchased = subscription?.quantity ?? 1;
  const used = usedSeats ?? 0;
  const remaining = Math.max(0, purchased - used);
  const overLimit = used > purchased;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" /> Team seats
        </CardTitle>
        <CardDescription>Each rep on your team uses one seat.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="text-2xl font-bold">{used}</span>
            <span className="text-muted-foreground"> / {purchased} seats used</span>
          </div>
          {overLimit ? (
            <Badge variant="destructive">Over limit</Badge>
          ) : remaining === 0 ? (
            <Badge variant="secondary">Full</Badge>
          ) : (
            <Badge variant="secondary">{remaining} free</Badge>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={overLimit ? "h-full bg-destructive" : "h-full bg-primary"}
            style={{ width: `${Math.min(100, (used / Math.max(1, purchased)) * 100)}%` }}
          />
        </div>

        {overLimit && (
          <div className="flex items-start gap-2 text-xs p-3 rounded-md bg-destructive/10 border border-destructive/30 text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>You have more reps than purchased seats. Add seats to keep all reps active.</span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/pricing">
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" /> Add seats
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground">
            $49/seat/month · changes prorated by Paddle.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
